import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { validatePostalCode, validateSiret } from '@/lib/validators';

interface ParsedTenant {
  row: number;
  tenant_type: 'individual' | 'professional';
  first_name: string;
  last_name: string;
  company_name: string;
  siret: string;
  vat_number: string;
  email: string;
  phone: string;
  adresse_bien: string;
  code_postal_bien: string;
  ville_bien: string;
  adresse_locataire: string;
  code_postal_locataire: string;
  ville_locataire: string;
  errors: string[];
  valid: boolean;
}

const TEMPLATE_HEADERS = [
  'Type (individual/professional)',
  'Prénom',
  'Nom',
  'Raison sociale',
  'SIRET',
  'N° TVA',
  'Email',
  'Téléphone',
  'Adresse du bien',
  'Code postal bien',
  'Ville bien',
  'Adresse locataire',
  'Code postal locataire',
  'Ville locataire',
];

export function ImportTenantsDialog() {
  const [open, setOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedSciId, setSelectedSciId] = useState<string>('');
  const [parsedData, setParsedData] = useState<ParsedTenant[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase.from('clients').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const { data: scis = [] } = useQuery({
    queryKey: ['scis'],
    queryFn: async () => {
      const { data, error } = await supabase.from('scis').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const downloadTemplate = () => {
    const csvContent = [
      TEMPLATE_HEADERS.join(','),
      'individual,Jean,Dupont,,,,jean.dupont@email.com,0612345678,123 rue du Bien,75001,Paris,456 rue Domicile,75002,Paris',
      'professional,Marie,Martin,Ma Société SARL,12345678901234,FR12345678901,marie@masociete.fr,0698765432,789 avenue Commerce,69001,Lyon,10 rue Siège,69002,Lyon',
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_locataires.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string): ParsedTenant[] => {
    const cleanText = text.replace(/^\ufeff/, '');
    const lines = cleanText.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) return [];

    // Auto-detect delimiter from header line
    const headerLine = lines[0];
    const delimiter = (headerLine.split(';').length > headerLine.split(',').length) ? ';' : ',';

    // Skip header row
    const dataLines = lines.slice(1);
    
    return dataLines.map((line, index) => {
      // Parse CSV line handling quoted values
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const errors: string[] = [];
      const tenantType = (values[0]?.toLowerCase() === 'professional' ? 'professional' : 'individual') as 'individual' | 'professional';
      const firstName = values[1] || '';
      const lastName = values[2] || '';
      const companyName = values[3] || '';
      const siret = values[4] || '';
      const vatNumber = values[5] || '';
      const email = values[6] || '';
      const phone = values[7] || '';
      const adresseBien = values[8] || '';
      const codePostalBien = values[9] || '';
      const villeBien = values[10] || '';
      const adresseLocataire = values[11] || '';
      const codePostalLocataire = values[12] || '';
      const villeLocataire = values[13] || '';

      // Validation
      if (tenantType === 'individual' && !firstName && !lastName) {
        errors.push('Prénom et nom requis pour un particulier');
      }
      if (tenantType === 'professional' && !companyName) {
        errors.push('Raison sociale requise pour un professionnel');
      }
      if (codePostalBien) {
        const postalValidation = validatePostalCode(codePostalBien);
        if (!postalValidation.valid) {
          errors.push('Code postal bien: ' + (postalValidation.message || 'invalide'));
        }
      }
      if (codePostalLocataire) {
        const postalValidation = validatePostalCode(codePostalLocataire);
        if (!postalValidation.valid) {
          errors.push('Code postal locataire: ' + (postalValidation.message || 'invalide'));
        }
      }
      if (tenantType === 'professional' && siret) {
        const siretValidation = validateSiret(siret);
        if (!siretValidation.valid) {
          errors.push(siretValidation.message || 'SIRET invalide');
        }
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push('Email invalide');
      }

      return {
        row: index + 2,
        tenant_type: tenantType,
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
        siret,
        vat_number: vatNumber,
        email,
        phone,
        adresse_bien: adresseBien,
        code_postal_bien: codePostalBien,
        ville_bien: villeBien,
        adresse_locataire: adresseLocataire,
        code_postal_locataire: codePostalLocataire,
        ville_locataire: villeLocataire,
        errors,
        valid: errors.length === 0,
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCSV(text);
      setParsedData(parsed);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClientId) throw new Error('Veuillez sélectionner un client');
      
      const validTenants = parsedData.filter(t => t.valid);
      if (validTenants.length === 0) throw new Error('Aucun locataire valide à importer');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const tenantsToInsert = validTenants.map(t => ({
        organization_id: profile.organization_id,
        client_id: selectedClientId,
        sci_id: selectedSciId || null,
        tenant_type: t.tenant_type,
        first_name: t.first_name || null,
        last_name: t.last_name || null,
        company_name: t.tenant_type === 'professional' ? t.company_name || null : null,
        siret: t.tenant_type === 'professional' ? t.siret || null : null,
        vat_number: t.tenant_type === 'professional' ? t.vat_number || null : null,
        email: t.email || null,
        phone: t.phone || null,
        adresse_bien: t.adresse_bien || null,
        code_postal_bien: t.code_postal_bien || null,
        ville_bien: t.ville_bien || null,
        adresse_locataire: t.adresse_locataire || null,
        code_postal_locataire: t.code_postal_locataire || null,
        ville_locataire: t.ville_locataire || null,
      }));

      const { error } = await supabase.from('tenants').insert(tenantsToInsert);
      if (error) throw error;

      return validTenants.length;
    },
    onSuccess: (count) => {
      toast.success(`${count} locataire(s) importé(s) avec succès`);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      resetDialog();
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const resetDialog = () => {
    setOpen(false);
    setParsedData([]);
    setFileName('');
    setSelectedClientId('');
    setSelectedSciId('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = parsedData.filter(t => t.valid).length;
  const invalidCount = parsedData.filter(t => !t.valid).length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetDialog();
      else setOpen(true);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Importer (CSV)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importer des locataires
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Template Download */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Télécharger le template</p>
                <p className="text-sm text-muted-foreground">
                  Utilisez ce fichier CSV comme modèle pour vos données
                </p>
              </div>
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Template CSV
              </Button>
            </div>
          </div>

          {/* Client/SCI Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>SCI (optionnel)</Label>
              <Select value={selectedSciId} onValueChange={setSelectedSciId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une SCI" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Aucune</SelectItem>
                  {scis.map((sci) => (
                    <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Fichier CSV</Label>
            <div className="flex gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="flex-1"
              />
            </div>
            {fileName && (
              <p className="text-sm text-muted-foreground">Fichier: {fileName}</p>
            )}
          </div>

          {/* Preview Table */}
          {parsedData.length > 0 && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-4 mb-2">
                <Badge variant="secondary" className="gap-1">
                  <Check className="h-3 w-3" /> {validCount} valide(s)
                </Badge>
                {invalidCount > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <X className="h-3 w-3" /> {invalidCount} erreur(s)
                  </Badge>
                )}
              </div>
              <ScrollArea className="flex-1 border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Ligne</TableHead>
                      <TableHead className="w-16">Statut</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>Erreurs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedData.map((tenant, idx) => (
                      <TableRow key={idx} className={!tenant.valid ? 'bg-destructive/10' : ''}>
                        <TableCell>{tenant.row}</TableCell>
                        <TableCell>
                          {tenant.valid ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={tenant.tenant_type === 'professional' ? 'default' : 'secondary'}>
                            {tenant.tenant_type === 'professional' ? 'Pro' : 'Particulier'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {tenant.tenant_type === 'professional' && tenant.company_name
                            ? tenant.company_name
                            : `${tenant.first_name} ${tenant.last_name}`.trim() || '-'}
                        </TableCell>
                        <TableCell>{tenant.email || '-'}</TableCell>
                        <TableCell>{tenant.city || '-'}</TableCell>
                        <TableCell className="text-sm text-destructive">
                          {tenant.errors.join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={resetDialog}>
              Annuler
            </Button>
            <Button 
              onClick={() => importMutation.mutate()}
              disabled={validCount === 0 || !selectedClientId || importMutation.isPending}
            >
              {importMutation.isPending ? 'Import...' : `Importer ${validCount} locataire(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}